import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { Modal } from "react-bootstrap";
import "../styles/Sidebar.scss";
import CommonButton from "./CommonButton";
import CommonSidebar from "./CommonSidebar";
import WhiteListDomainModal from "../Dashboard/ExposureMap/WhiteListDomainModal";
import { getUserFlags, hasPermission, isOrgAdminOnly } from "../helper";

import foreseeLogo from '../NovaWidget/assets/foresee-eagle.png';
const EDIT_ICON = "https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/edit-icon.svg";


const Sidebar = ({ collapsed, isMobileOpen, onClose, planInfo, onToggle }) => {
    const [showWhiteListModal, setShowWhiteListModal] = useState(false);
    const [showUpgradeModal, setShowUpgradeModal] = useState(false);
    const [toast, setToast] = useState({ show: false, message: '', type: 'success' });

    const isAdmin = isOrgAdminOnly();

    const showToast = (message, type = 'success') => {
        setToast({ show: true, message, type });
        setTimeout(() => setToast({ show: false, message: '', type: 'success' }), 4000);
    };
    const handleOpenModal = async () => {
        setShowWhiteListModal(true);
    };

    const handleCloseModal = () => {
        setShowWhiteListModal(false);
    };

    React.useEffect(() => {
        const handleOpenUpgrade = () => setShowUpgradeModal(true);
        window.addEventListener('openUpgradeModal', handleOpenUpgrade);
        return () => window.removeEventListener('openUpgradeModal', handleOpenUpgrade);
    }, []);

    const flags = getUserFlags();
    const showUserManagement = flags.is_org_admin


    const showBrandProtection = hasPermission('view_brand_risk');

    const getCompanyName = localStorage.getItem("email");

    const getFormattedDomain = () => {
        if (getCompanyName && getCompanyName.includes('@')) {
            const domain = getCompanyName.split('@')[1].split('.')[0];
            return domain.charAt(0).toUpperCase() + domain.slice(1);
        }
        return '';
    };

    // Get current plan index from pricingPlans based on planInfo.plan_tier
    const getCurrentPlanIndex = () => {
        if (!planInfo?.plan_tier) return -1;
        return planInfo.plan_tier - 1; // plan_tier is 1-indexed
    };

    // Get next plan name for upgrade button
    const getNextPlanName = () => {
        const currentIndex = getCurrentPlanIndex();
        if (currentIndex >= 0 && currentIndex < pricingPlans.length - 1) {
            return pricingPlans[currentIndex + 1]?.name || 'Foundation';
        }
        return 'Foundation'; // Default
    };

    // Check if a plan is the current active plan
    const isCurrentPlan = (planIndex) => {
        return planIndex === getCurrentPlanIndex();
    };

    const handlePlanClick = async (plan) => {
        try {
            const emailId = localStorage.getItem('email') || '';

            const response = await fetch('https://api.foreseesecurity.io/nmt/web/api/v1/create-lead', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    name: '',
                    emailId: emailId,
                    phoneNumber: '',
                    address: '',
                    description: `User clicked on ${plan.name} plan`,
                    lead_type: 'contact'
                })
            });

            if (response.ok) {
                setShowUpgradeModal(false);
                showToast(`Thank you for your interest in ${plan.name} plan! Our team will contact you soon.`, 'success');
            } else {
                showToast('Something went wrong. Please try again.', 'error');
            }
        } catch (error) {
            console.error('Error creating lead:', error);
            showToast('Something went wrong. Please try again.', 'error');
        }
    };

    const { pathname, search } = useLocation();
    const isHub = pathname === '/product-dashboard' || pathname === '/user-management' || pathname === '/settings';
    const view = new URLSearchParams(search).get('view');

    
    const inEasmContext = (pathname === '/dashboard' && view === 'easm') || pathname.startsWith('/brand-protection');

    const DASHBOARD_ICON = "https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/dashboard.svg";
    const SETTINGS_ICON = "https://nmtsecurity-publicbucket.s3.us-west-2.amazonaws.com/dashboard/settings.svg";
    const BRAND_PROTECTION_ICON = (
        <svg className='icon' width="16" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            <polyline points="9 12 11 14 15 10" />
        </svg>
    );
    const EASM_ICON = (
        <svg className='icon' width="16" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><ellipse cx="12" cy="12" rx="4" ry="10" /><line x1="2" y1="12" x2="22" y2="12" />
        </svg>
    );
    const TPCRM_ICON = (
        <svg className='icon' width="16" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="18" cy="5" r="3" /><circle cx="6" cy="12" r="3" /><circle cx="18" cy="19" r="3" /><line x1="8.6" y1="10.6" x2="15.4" y2="6.4" /><line x1="8.6" y1="13.4" x2="15.4" y2="17.6" />
        </svg>
    );
    const COMPLIANCE_ICON = (
        <svg className='icon' width="16" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="5" y="4" width="14" height="18" rx="2" /><path d="M9 4V2.5h6V4" /><path d="M9 13l2 2 4-4" />
        </svg>
    );
    const BACK_ICON = (
        <svg className='icon' width="16" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" />
        </svg>
    );

    const userMgmtItem = { to: '/user-management', label: 'User Management', icon: '/assets/user-setting.svg', active: pathname.startsWith('/user-management') };
    const settingsItem = { to: '/settings', label: 'Settings', icon: SETTINGS_ICON, active: pathname.startsWith('/settings') };

    const navItems = isHub
        ? [
            { to: '/product-dashboard', label: 'Dashboard', end: true, icon: DASHBOARD_ICON },
            ...(showUserManagement ? [userMgmtItem] : []),
            settingsItem,
        ]
        : [
            { to: '/dashboard?view=easm', label: 'EASM', svgIcon: EASM_ICON, active: pathname === '/dashboard' && view === 'easm' },
            { to: '/tpcrm/dashboard', label: 'TPCRM', svgIcon: TPCRM_ICON, active: pathname.startsWith('/tpcrm') },
            ...(showBrandProtection && inEasmContext ? [{ to: '/brand-protection', label: 'Brand Protection', svgIcon: BRAND_PROTECTION_ICON, active: pathname.startsWith('/brand-protection') }] : []),
            { to: '/dashboard?view=compliance', label: 'Continuous Compliance', svgIcon: COMPLIANCE_ICON, active: pathname === '/dashboard' && view === 'compliance' },
            ...(showUserManagement ? [userMgmtItem] : []),
            settingsItem,
            { to: '/product-dashboard', label: 'Back to Dashboard', end: true, svgIcon: BACK_ICON },
        ];

    // Brand header — NMT logo + name + org domain, with the edit action that
    // opens the domain-whitelist modal. Collapsed shows just the mark.
    const brandHeader = collapsed ? (
        <div className="logoContainer">
            <img src={foreseeLogo} alt="Foresee Security" className="sb-brand-logo--sm" />
        </div>
    ) : (
        <div className="logoContainer">
            <div className="sb-brand-row">
                <div className="sb-brand">
                    <img src={foreseeLogo} alt="Foresee Security" className="sb-brand-logo" />
                    <div className="sb-brand-text">
                        <span className="sb-brand-name">FORESEE</span>
                        <span className="sb-brand-sub">SECURITY</span>
                    </div>
                </div>
                {isAdmin && (
                    <span onClick={handleOpenModal} className="sb-brand-edit">
                        <img alt="edit icon" height="20" src={EDIT_ICON} />
                    </span>
                )}
            </div>
        </div>
    );

    // Upgrade card — bottom of the rail, hidden when collapsed.
    const upgradeFooter = !collapsed ? (
        <div className="upgrade_wrp mt-auto">
            <h4>Upgrade </h4>
            <p className="color_white">Current Plan: <b className="text-nowrap">{planInfo?.plan_name}</b></p>
            <div onClick={() => setShowUpgradeModal(true)}>
                <CommonButton text={`Upgrade to ${getNextPlanName()}`} className={'upgrade_btn'} />
            </div>
        </div>
    ) : null;

    return (
        <>
            <CommonSidebar
                mode="link"
                collapsed={collapsed}
                onToggle={onToggle}
                isMobileOpen={isMobileOpen}
                onClose={onClose}
                header={brandHeader}
                groups={[{ label: 'Platform', items: navItems }]}
                footer={planInfo?.plan_name !== 'Secure+' && upgradeFooter}
            />

            {/* Whitelist Modal */}
            {showWhiteListModal && (
                <WhiteListDomainModal show={showWhiteListModal} onHide={handleCloseModal} />
            )}

            {/* Upgrade Plans Modal */}
            <Modal
                show={showUpgradeModal}
                onHide={() => setShowUpgradeModal(false)}
                size="xl"
                centered
                className="pricing_modal"
            >
                <Modal.Header closeButton>
                    <Modal.Title>Choose Your Plan</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <div className="pricing_cards">
                        <div className="row justify-content-center">
                            {pricingPlans.map((plan, index) => {
                                return (
                                    <div className="col-md-3" key={index}>
                                        <div className={`pricing_card ${plan.isHidePrice ? 'hide_price' : ''}`}>
                                            <div className="card_header">
                                                <div className='d-flex align-items-center justify-content-between'>
                                                    <span className='cm_chip_sm'>{plan.name}</span>
                                                </div>
                                            </div>
                                            <div className="card_description">
                                                <h3>{plan.description}</h3>
                                                <p>{plan.subtitle}</p>
                                            </div>
                                            <div className="card_features">
                                                <ul>
                                                    {plan.features.map((feature, featureIndex) => (
                                                        <li key={featureIndex}>{feature}</li>
                                                    ))}
                                                </ul>
                                            </div>
                                            <div className="card_action">
                                                <button
                                                    className={`btn ${isCurrentPlan(index) ? 'btn-secondary' : 'btn-primary'}`}
                                                    onClick={() => !isCurrentPlan(index) && handlePlanClick(plan)}
                                                    disabled={isCurrentPlan(index)}
                                                    style={isCurrentPlan(index) ? { cursor: 'not-allowed', opacity: 0.7 } : {}}
                                                >
                                                    {isCurrentPlan(index) ? 'Current Plan' : plan.buttonText}
                                                </button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            {/* Custom Toast */}
            {toast.show && (
                <div className={`custom-toast ${toast.type}`}>
                    <span className="icon">
                        {toast.type === 'success' ? '✓' : '✕'}
                    </span>
                    <span className="message">{toast.message}</span>
                    <button
                        className="close-btn"
                        onClick={() => setToast({ show: false, message: '', type: 'success' })}
                    >
                        ×
                    </button>
                </div>
            )}
        </>
    );
};

export default Sidebar;




const pricingPlans = [
    {
        isHidePrice: true,
        name: 'Proof of Concept',
        redirectUrl: 'https://dashboard.foreseesecurity.io/dashboard',
        originalPrice: 0,
        discountedPrice: 0,
        isTrial: false,
        discount: null,
        description: 'Baseline Visibility',
        subtitle: 'Best for evaluation & awareness',
        features: [
            'One-time external exposure scan (onboarding)',
            'Visibility across 6 risk domains',
            'Weekly CXO exposure email',
            'Public asset discovery',
            'Risk registry (add & track risks)',
            'Standard security policies'
        ],
        buttonText: 'Get Started',
        popular: false,
        monthlyPricing: {
            usd: { original: 0, discounted: 0, discount: '' },
            inr: { original: 0, discounted: 0, discount: '' }
        },
        annualPricing: {
            usd: { original: 0, discounted: 0, discount: '' },
            inr: { original: 0, discounted: 0, discount: '' }
        }
    },
    {
        isHidePrice: true,
        name: 'Essential',
        redirectUrl: 'https://dashboard.foreseesecurity.io/dashboard',
        originalPrice: 1000,
        discountedPrice: 700,
        isTrial: true,
        discount: null,
        description: 'Security Hygiene for Growing Teams',
        subtitle: 'Best for startups & early-stage companies',
        features: [
            'Weekly external exposure scans',
            '6-domain risk visibility',
            'CXO, summary & detailed reports',
            'Cloud & on-prem asset integration',
            'Vulnerability scanning (50 assets)',
            'TPCRM for 50 vendors (monthly scan)',
            'Risk registry & reporting',
            'CIS & ISO 27001 readiness',
            'Phishing simulation (annual, 100 users)',
            'Annual infrastructure VAPT',
            '2 hrs/month CISO-as-a-Service'
        ],
        buttonText: 'Get Started',
        popular: false,
        monthlyPricing: {
            usd: { original: 1000, discounted: 1000, discount: '' },
            inr: { original: 90000, discounted: 90000, discount: '' }
        },
        annualPricing: {
            usd: { original: 9600, discounted: 9600, discount: '20% Discount' },
            inr: { original: 864000, discounted: 864000, discount: '20% Discount' }
        }
    },
    {
        isHidePrice: true,
        name: 'Foundation',
        redirectUrl: '/contact',
        originalPrice: 2500,
        discountedPrice: 1375,
        description: 'Compliance-Driven Security',
        subtitle: 'Best for regulated SaaS, NBFCs & fintechs',
        features: [
            'Everything in Essential, plus',
            'Domain impersonation detection & takedown',
            'Exploitability-based vulnerability prioritization',
            'Patch & remediation guidance',
            'TPCRM auto-scheduling + questionnaires',
            'Vendor assessment reports & heatmap',
            'Compliance: RBI, SEBI, DPDP, DORA, GDPR',
            'Evidence upload with FORESEE (AI CISO) guidance',
            'Policy customization with TAM',
            '8 hrs/month CISO-as-a-Service',
            'DPO-as-a-Service (2 hrs/month)',
            'Application architecture review (1 app)',
            'Board-level reporting pack',
            'SAST (4× per year)'
        ],
        buttonText: 'Get Started',
        popular: true,
        monthlyPricing: {
            usd: { original: 2500, discounted: 2500, discount: '' },
            inr: { original: 225000, discounted: 225000, discount: '' }
        },
        annualPricing: {
            usd: { original: 24000, discounted: 24000, discount: '20% Discount' },
            inr: { original: 2160000, discounted: 2160000, discount: '20% Discount' }
        }
    },
    {
        name: 'Secure+',
        redirectUrl: '/contact',
        originalPrice: null,
        discountedPrice: 5000,
        discount: null,
        description: 'Full CISO Copilot & SOC',
        subtitle: 'Best for enterprises, BFSI & board-driven organizations',
        features: [
            'Everything in Foundation, plus',
            'Weekly vendor risk scans',
            'SOC 24×7 monitoring',
            'Dedicated CISO support',
            'DPO-as-a-Service (8 hrs/month)',
            'Application architecture reviews (up to 5 apps)',
            'Phishing simulations (quarterly, all employees)',
            'VAPT (bi-annual infra & application)',
            'Advanced SAST (10× per year)',
            'Virtual + on-site security awareness training'
        ],
        buttonText: 'Get Started',
        popular: false,
        monthlyPricing: {
            usd: { original: null, discounted: 5000 },
            inr: { original: null, discounted: 450000 }
        },
        annualPricing: {
            usd: 'On-Request',
            inr: 'On-Request'
        }
    }
];
